<template>
  <ValidationObserver slim ref="observer" v-slot="{ invalid }">
    <EuiDialog v-if="open" :open="open" @onClose="close" size="medium">
      <template #header>
        {{ $t('signworkflows.sign.stepper.title') }}
      </template>
      <EuiStepper horizontal>
        <EuiStepperItem v-if="signatureCodeIsMandatory()" class="eui-u-cursor-pointer"
                        @click.native="goToStep(1)"
                        :state="signDialogData.currentStep === 1 ? 'inProgress' : 'valid'"
                        :title="$t('signworkflows.sign.stepper.step1')"/>
        <EuiStepperItem class="eui-u-cursor-pointer"
                        v-if="hasParaphs"
                        @click.native="goToStep(2)"
                        :state="signDialogData.currentStep < 2 ? 'disabled' : signDialogData.currentStep < 3 ? signDialogData.signatureStatus === 'error' ? 'error' : 'inProgress' : 'valid'"
                        :title="$t('signworkflows.sign.stepper.step2')"/>
        <EuiStepperItem class="eui-u-cursor-pointer"
                        v-if="hasSigns"
                        @click.native="goToStep(3.1)"
                        :state="signDialogData.currentStep < 3 ? 'disabled' : signDialogData.currentStep < 4 ? signDialogData.signatureStatus === 'error' ? 'error' : 'inProgress' : 'valid'"
                        :title="$t('signworkflows.sign.stepper.step3')"/>
        <EuiStepperItem :state="signDialogData.currentStep < 4 ? 'disabled' : signDialogData.signatureStatus === 'error' ? 'error' : 'valid'"
                        :title="signDialogData.signatureStatus === 'success' ? $t('signworkflows.sign.stepper.step4') : $t('signworkflows.sign.stepper.step4.error')"/>
      </EuiStepper>
      <EuiDivider secondary/>
      <template v-if="signDialogData.certificate">
        <SendCode v-if="signDialogData.currentStep === 1" :isUsbSignAvailable="!hasParaphs"/>
        <Paraph :isUsbSignAvailable="!hasParaphs" :displayCode="signatureCodeIsMandatory()" ref="paraphs" :hasNextStep="hasSigns" v-if="signDialogData.currentStep === 2" :signworkflows="signworkflows" @next="goToStep(3.1)"/>
        <Signature :isUsbSignAvailable="!hasParaphs" :displayCode="signatureCodeIsMandatory()" ref="signature" v-if="signDialogData.currentStep === 3.1" :signworkflows="signworkflows" @next="goToStep(4)"/>
        <SignatureUsb v-if="signDialogData.currentStep === 3.2" :signworkflows="signworkflows"/>
        <Status v-if="signDialogData.currentStep === 4"/>
      </template>
      <template #footer>
        <!-- CODE -->
        <template v-if="signDialogData.currentStep === 1">
          <EuiButton color="primary"
                     variant="text"
                     @click.prevent="close">
            {{ $t('button.cancel') }}
          </EuiButton>
          <EuiButton color="primary"
                     variant="raised"
                     type="submit"
                     :disabled="invalid"
                     form="signatureCodeForm">
            {{ $t('button.next') }}
          </EuiButton>
        </template>
        <!-- PARAPH -->
        <template v-else-if="signDialogData.currentStep === 2">
          <EuiButton color="primary"
                     @click="goToStep(1)">
            {{ $t('button.back') }}
          </EuiButton>
          <EuiButton v-if="hasSigns"
                     color="primary"
                     variant="raised"
                     type="submit"
                     :disabled="!signStepValid"
                     form="paraphForm">
            {{ $t('button.next') }}
          </EuiButton>
          <EuiButton v-else
                     color="primary"
                     variant="raised"
                     type="submit"
                     :disabled="signStepValid && (invalid || !canSign || signDialogData.isLoading)"
                     form="paraphForm">
            {{ $t('button.sign') }}
          </EuiButton>
        </template>
        <!-- SIGN -->
        <template v-else-if="signDialogData.currentStep === 3.1">
          <EuiButton color="primary" variant="text" @click.prevent="signDialogData.hasCanceled = true; goBack()">
            {{ $t('button.cancel') }}
          </EuiButton>
          <EuiButton color="primary"
                     variant="raised"
                     type="submit"
                     :disabled="signStepValid && (invalid || !canSign || signDialogData.isLoading)"
                     form="signatureForm">
            {{ $t('button.sign') }}
          </EuiButton>
        </template>
        <!-- SIGNUSB -->
        <template v-else-if="signDialogData.currentStep === 4.1">
          <EuiButton color="primary" variant="text" @click.prevent="signDialogData.hasCanceled = true; goBack()">
            {{ $t('button.cancel') }}
          </EuiButton>
          <EuiButton color="primary"
                     variant="raised"
                     type="submit"
                     :disabled="signStepValid && (invalid || !canSign || signDialogData.isLoading)"
                     form="signatureForm">
            {{ $t('button.sign') }}
          </EuiButton>
        </template>
        <!-- VALIDATION -->
        <template v-else-if="signDialogData.currentStep === 4 || signDialogData.currentStep === 4.2">
          <EuiButton color="primary" variant="raised" @click="validate" v-if="signDialogData.signatureStatus === 'success'">
            {{ $t('button.close') }}
          </EuiButton>
          <EuiButton v-else-if="signDialogData.signatureStatus" color="primary" variant="raised" @click.prevent="onError">
            {{ $t('button.back') }}
          </EuiButton>
        </template>
      </template>
    </EuiDialog>
  </ValidationObserver>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';
import SendCode from './steps/SendCode';
import Signature from './steps/Signature';
import Paraph from './steps/Paraph';
import Status from './steps/Status';
import SignatureUsb from './steps/SignatureUsb';
import axios from 'axios';

export default {
  name: 'SignDialog',
  components: {
    Signature,
    Paraph,
    SendCode,
    SignatureUsb,
    Status,
  },
  provide() {
    const { signDialogData } = this;

    return { signDialogData };
  },
  computed: {
    ...mapGetters({
      user: 'ModuleEdocSign/auth/user',
      options: 'ModuleEdocSign/auth/options',
      searchParams: 'ModuleEdocSign/parapheurs/searchParams',
    }),
    canSign() {
      if (!this.signatureCodeIsMandatory()) {
        return true;
      } else {
        return this.signDialogData.agreement;
      }
    },
    signStepValid() {
      if (this.signDialogData.selectedSignatureMode === 'logo-file') return this.signDialogData.signatureImage !== undefined;
      else return true;
    },
  },
  props: {
    textZoneModels: {
      type: Array,
    },
    signworkflows: {
      type: Array,
      required: true,
    },
    open: {
      type: Boolean,
    },
    hasSigns: {
      type: Boolean,
    },
    hasParaphs: {
      type: Boolean,
    },
  },
  model: {
    prop: 'open',
    event: 'change',
  },
  watch: {
    signworkflows(value) {
      if (value && value[0]) {
        this.signDialogData.isLastSignatory = (this.signworkflows[0].nbSteps === this.signworkflows[0].nbStepsRealized + 1);
      }
      this.signDialogData.isSignatureCodeMandatory = this.signatureCodeIsMandatory();
      if (!this.signDialogData.isSignatureCodeMandatory) {
        this.light = true;
        this.signDialogData.goToStep(2);
      }
      this.signDialogData.captiveZones = [];
      this.signworkflows.forEach((signworkflow) => {
        signworkflow.steps.forEach((step) => {
          if (step.userCanSign) {
            step.captiveZones.forEach((captiveZone) => {
              if (captiveZone.position && captiveZone.zoneType === 'textfield') {
                this.signDialogData.captiveZones.push({
                  zoneType: 'textfield',
                  id: captiveZone.id,
                  content: this.textZoneModels.find((text) => {
                    return text.id === captiveZone.id;
                  }).model,
                });
              }
            });
          }
        });
      });
      if (this.signworkflows[0] && this.signworkflows[0].hasOnlyTextFields) {
        this.goToStep(4);
      }
    },
    open(value) {
      if (value) {
        if (this.user === undefined) {
          this.getUserInfos()
            .then(() => {
              this.signDialogData.certificate = JSON.parse(JSON.stringify(this.user));
              this.signDialogData.certificate.signatureRetrieveMode = 'sms';
              this.signDialogData.isSignatureCodeMandatory = this.signatureCodeIsMandatory();
              this.signDialogData.lastCertificateGenerationDate = this.user.lastCertificateGenerationDate;
              this.light = (this.user.lastCertificateGenerationDate !== null &&
                this.user.lastCertificateGenerationDate !== undefined) || !this.signDialogData.isSignatureCodeMandatory;
              if (this.options && this.options.signerSignWithSketchPad) {
                this.signDialogData.canSkectchSignature = parseInt(this.options.signerSignWithSketchPad.value) === 1;
              }
              /* if (this.light) {
                this.hasSigns ? this.signDialogData.currentStep = 2.1 : this.hasParaphs ? this.signDialogData.currentStep = 3 : console.log('test');
              } */
            });
        } else {
          this.signDialogData.isSignatureCodeMandatory = this.signatureCodeIsMandatory();
          this.signDialogData.lastCertificateGenerationDate = this.user.lastCertificateGenerationDate;
          this.signDialogData.certificate = JSON.parse(JSON.stringify(this.user));
          if (this.signDialogData.certificate.logo) {
            this.signatureImage = {
              fileContent: this.signDialogData.certificate.logo,
            };
          }
          this.signDialogData.certificate.signatureRetrieveMode = 'sms';
          this.light = ((this.user.lastCertificateGenerationDate !== null &&
            this.user.lastCertificateGenerationDate !== undefined) || !this.signDialogData.isSignatureCodeMandatory);
          /* if (this.light) {
            this.hasSigns ? this.signDialogData.currentStep = 2.1 : this.hasParaphs ? this.signDialogData.currentStep = 3 : console.log('test');
          } */
          if (this.options && this.options.signerSignWithSketchPad) {
            this.signDialogData.canSkectchSignature = parseInt(this.options.signerSignWithSketchPad.value) === 1;
          }
        }
        this.signDialogData.isLastSignatory = (this.signworkflows[0].nbSteps === this.signworkflows[0].nbStepsRealized + 1);
        if (this.user.lastCertificateGenerationDate) this.goToStep(2);
      }
    },
  },
  data() {
    return {
      light: false,
      signDialogData: this.initialState(),
    };
  },
  methods: {
    ...mapActions({
      storeGetSignworkflows: 'ModuleEdocSign/parapheurs/getSignworkflows',
      getUserInfos: 'ModuleEdocSign/auth/getUserInfos',
      sendSignature: 'ModuleEdocSign/parapheurs/sendSignature',
      finishLoading: 'application/finishLoading',
      getUsageDatas: 'ModuleEdocSign/auth/getUsageDatas',
    }),
    initialState() {
      return {
        isSignaturePadVisible: false,
        isSignatureCodeFormValid: false,
        pngSignature: undefined,
        agreement: false,
        currentStep: 1,
        previousStep: undefined,
        usbSignatureInfo: '',
        selectedSignatureMode: 'auto-generated',
        hasCanceled: false,
        hasSignatureCode: false,
        signatureCode: '',
        signatureImage: undefined,
        goToStep: this.goToStep,
        isLastSignatory: false,
        isSignatureCodeMandatory: true,
        certificate: undefined,
        canSkectchSignature: true,
        signatureStatus: '',
        isLoading: undefined,
      };
    },
    signatureCodeIsMandatory() {
      let isMandatory = false;
      this.signworkflows.forEach((signworkflow) => {
        signworkflow.steps.forEach((step) => {
          if (step.userId === this.user.userId && step.isNextStep && step.signerOnlyValidate === '0') {
            isMandatory = true;
          }
        });
      });
      return isMandatory || this.hasParaphs;
    },
    goBack() {
      if (this.signDialogData.previousStep) {
        this.signDialogData.currentStep = this.signDialogData.previousStep;
      }
    },
    goToStep(step) {
      if (this.signDialogData.signatureStatus === 'success' && this.signDialogData.currentStep === 4) {
        return;
      }
      switch (step) {
        case 2:
          if (!this.hasParaphs) {
            this.goToStep(3.1);
          } else {
            this.signDialogData.previousStep = this.signDialogData.currentStep;
            this.signDialogData.currentStep = step;
          }
          break;
        case 3.1:
          if (!this.hasSigns) {
            this.goToStep(4);
          } else {
            this.signDialogData.previousStep = this.signDialogData.currentStep;
            this.signDialogData.currentStep = step;
          }
          break;
        case 4:
          this.signDialogData.previousStep = this.signDialogData.currentStep;
          this.signDialogData.currentStep = step;
          this.sign();
          break;
        case 4.1:
          this.signDialogData.previousStep = this.signDialogData.currentStep;
          this.signDialogData.currentStep = step;
          break;
        case 4.2:
          this.signDialogData.previousStep = this.signDialogData.currentStep;
          this.signDialogData.currentStep = step;
          break;
        default:
          this.signDialogData.previousStep = this.signDialogData.currentStep;
          this.signDialogData.currentStep = step;
          break;
      }
    },
    validate() {
      this.close();
      if (!this.$store.state.ModuleEdocSign.auth.session.guest) {
        this.$router.push({ path: '/signworkflows/inbox' });
      } else {
        this.$router.push({ name: 'guestSignworkflows' });
      }
    },
    onError() {
      if (this.hasParaphs) {
        this.goToStep(2);
      } else if (this.hasSigns) {
        this.goToStep(3.1);
      } else {
        this.close();
      }
    },
    resetData() {
      Object.assign(this.$data.signDialogData, this.initialState());
    },
    close() {
      this.resetData();
      this.$emit('change', false);
      this.$emit('close');
    },
    sign() {
      const list = this.signworkflows;
      const promises = [];
      for (let i = 0; i < list.length; i++) {
        if (!(list[i].nbStepsRealized >= list[i].nbSteps)) {
          promises.push(this.sendSignature({
            signBookId: list[i].signBookId,
            signatureCode: (!list[i].hasOnlyTextFields && (this.signatureCodeIsMandatory() || this.hasParaphs)) ? this.signDialogData.signatureCode : undefined,
            captiveZones: this.signDialogData.captiveZones,
          }));
        }
      }
      axios.all(promises)
        .then(() => {
          this.signDialogData.signatureStatus = 'success';
          this.signDialogData.error = '';
          if (this.$route.name === 'guestSignworkflows' || !this.$store.state.ModuleEdocSign.auth.session.guest) {
            return this.storeGetSignworkflows(this.searchParams)
              .then(() => {
                this.getUsageDatas()
                  .then(() => {
                    this.goToStep(4);
                  });
              });
          }
        }).catch((e) => {
          this.signDialogData.signatureStatus = 'error';
          this.signDialogData.signatureCode = '';
          this.signDialogData.error = e.data.message;
        });
    },
  },
};
</script>
